import { useEffect, useState, useCallback } from "react";
import { api, apiHref } from "@/lib/api";
import { usePlant } from "@/context/PlantContext";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Calendar as CalIcon, Printer, ChevronLeft, ChevronRight, FileDown, Upload } from "lucide-react";
import CsvImportDialog from "@/components/CsvImportDialog";
import { API_BASE } from "@/lib/api";
import { localToday, shiftLocalDate, formatLocalTime, toPlantISODate } from "@/lib/dates";

const CHART_COLOR = "#F57C00";

const fmtDate = (iso) => {
  if (!iso) return "";
  // en-AU + explicit plant TZ so "Monday, 11 September 2026" reads the same
  // for accounts folk on-site AND remote viewers in the US.
  const d = new Date(iso + "T00:00:00+10:00");
  return d.toLocaleDateString("en-AU", {
    weekday: "long", day: "2-digit", month: "long", year: "numeric",
    timeZone: "Australia/Hobart",
  });
};
// Convert a Date picker value → YYYY-MM-DD in PLANT time.
const toISO = (d) => toPlantISODate(d);

export default function Reports() {
  const { plantId } = usePlant();
  const [importOpen, setImportOpen] = useState(false);
  const [calKey, setCalKey] = useState(0);
  const today = localToday();

  // ── Rolling summary (14-day) ─────────────────────────────────────────────
  const [summaryDays, setSummaryDays] = useState(14);
  const [summary, setSummary] = useState(null);

  // ── Daily report (single day) ────────────────────────────────────────────
  const [reportDate, setReportDate] = useState(today);
  const [daily, setDaily] = useState(null);
  const [dateOpen, setDateOpen] = useState(false);
  const [loadingDaily, setLoadingDaily] = useState(false);

  const loadSummary = useCallback(() => {
    api.get(`/reports/summary?days=${summaryDays}`).then((r) => setSummary(r.data));
  }, [summaryDays]);

  const loadDaily = useCallback(() => {
    setLoadingDaily(true);
    api
      .get(`/reports/daily?date=${reportDate}${plantId ? `&plant_id=${plantId}` : ""}`)
      .then((r) => setDaily(r.data))
      .finally(() => setLoadingDaily(false));
  }, [reportDate, plantId]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { loadDaily(); }, [loadDaily]);

  // ── EFT orders report ────────────────────────────────────────────────────
  const monthStart = today.slice(0, 8) + "01";
  const [eftStart, setEftStart] = useState(monthStart);
  const [eftEnd, setEftEnd] = useState(today);
  const [eft, setEft] = useState(null);
  const [eftStartOpen, setEftStartOpen] = useState(false);
  const [eftEndOpen, setEftEndOpen] = useState(false);
  const loadEft = useCallback(() => {
    api.get(`/reports/eft?start=${eftStart}&end=${eftEnd}`).then((r) => setEft(r.data)).catch(() => {});
  }, [eftStart, eftEnd]);
  useEffect(() => { loadEft(); }, [loadEft]);

  const tooltipStyle = { backgroundColor: "#121214", border: "1px solid #27272A", borderRadius: 0, fontFamily: "JetBrains Mono" };
  const shiftDate = (delta) => setReportDate(shiftLocalDate(reportDate, delta));

  const printDaily = () => window.print();

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="reports-page">
      {/* Print-friendly rules — hide the app chrome when printing */}
      <style>{`
        @media print {
          body { background: #fff !important; color: #000 !important; }
          [data-print-hide], nav, aside, header { display: none !important; }
          [data-testid="reports-page"] { padding: 0 !important; }
          .card-industrial { border: 1px solid #000 !important; background: #fff !important; break-inside: avoid; page-break-inside: avoid; }
          .label-tag, .mono, .heading, h1, h2, h3, td, th { color: #000 !important; }
          .text-primary-orange { color: #d84a00 !important; }
          .border-industrial { border-color: #000 !important; }
          .bg-primary-orange { background: #d84a00 !important; }
          .bg-surface-2, .bg-surface { background: #f5f5f5 !important; }
          .no-print, [data-print-hide-inline] { display: none !important; }
          [data-print-only] { display: block !important; }
        }
        [data-print-only] { display: none; }
      `}</style>

      <div data-print-hide className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-tag">ANALYTICS</div>
          <h1 className="heading text-3xl font-black uppercase">Reports</h1>
        </div>
        <Button data-testid="import-daily-totals-btn" variant="outline" onClick={() => setImportOpen(true)} className="rounded-none border-industrial uppercase tracking-widest text-xs font-bold"><Upload className="w-4 h-4 mr-1 text-primary-orange" />Import history</Button>
      </div>
      <CsvImportDialog entity="daily_totals" open={importOpen} onOpenChange={setImportOpen} onDone={() => setCalKey((k) => k + 1)} />

      {/* ─── MONTHLY LOADS CALENDAR ─────────────────────────────────── */}
      <MonthlyLoadsCard key={calKey} plantId={plantId} />

      {/* ─── DAILY REPORT ────────────────────────────────────────────────── */}
      <div className="card-industrial p-5 space-y-4" data-testid="daily-report-panel">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="label-tag">DAILY REPORT</div>
            <h2 className="heading text-xl font-black uppercase" data-testid="daily-report-heading">
              {fmtDate(reportDate)}
            </h2>
          </div>
          <div className="flex items-center gap-2" data-print-hide>
            <button
              type="button"
              onClick={() => shiftDate(-1)}
              data-testid="daily-prev-day"
              className="border border-industrial p-2 hover:border-primary-orange hover:text-primary-orange"
              title="Previous day"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <Popover open={dateOpen} onOpenChange={setDateOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  data-testid="daily-date-btn"
                  className="border border-industrial px-3 py-2 uppercase tracking-widest text-xs mono inline-flex items-center gap-2 hover:border-primary-orange hover:text-primary-orange"
                >
                  <CalIcon className="w-3.5 h-3.5" /> {reportDate}
                </button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-auto p-0" data-testid="daily-date-popover">
                <Calendar
                  mode="single"
                  selected={new Date(reportDate + "T00:00:00")}
                  onSelect={(d) => { if (d) { setReportDate(toISO(d)); setDateOpen(false); } }}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
            <button
              type="button"
              onClick={() => shiftDate(1)}
              data-testid="daily-next-day"
              className="border border-industrial p-2 hover:border-primary-orange hover:text-primary-orange"
              title="Next day"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => setReportDate(today)}
              data-testid="daily-today-btn"
              className="border border-industrial px-3 py-2 uppercase tracking-widest text-xs mono hover:border-primary-orange hover:text-primary-orange"
            >
              Today
            </button>
            <Button
              onClick={printDaily}
              data-testid="daily-print-btn"
              className="rounded-none bg-primary-orange hover:bg-[rgb(var(--primary-hover))] uppercase tracking-widest text-xs font-bold"
            >
              <Printer className="w-4 h-4 mr-1" /> Print / PDF
            </Button>
          </div>
        </div>

        {loadingDaily && <div className="label-tag text-primary-orange pulse-orange">Compiling…</div>}

        {daily && !loadingDaily && (
          <>
            {/* Totals grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="daily-totals">
              <Stat label="Loads" value={daily.totals.loads} />
              <Stat label="m³ delivered" value={daily.totals.m3.toFixed(1)} accent />
              <Stat label="Revenue" value={`$${daily.totals.revenue.toFixed(2)}`} accent />
              <Stat label="Jobs scheduled" value={daily.totals.jobs_scheduled} />
              <Stat label="Jobs completed" value={daily.totals.jobs_completed} />
              <Stat
                label="Cancelled"
                value={`${daily.totals.cancelled_loads} · ${daily.totals.cancelled_m3.toFixed(1)}m³`}
                red={daily.totals.cancelled_loads > 0}
              />
            </div>

            {/* Breakdowns */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <BreakdownTable
                title="By Mix"
                testid="daily-by-mix"
                rows={daily.by_mix}
                keyField="mix"
              />
              <BreakdownTable
                title="By Truck"
                testid="daily-by-truck"
                rows={daily.by_truck}
                keyField="truck"
              />
              <BreakdownTable
                title="By Customer"
                testid="daily-by-customer"
                rows={daily.by_customer}
                keyField="customer"
              />
            </div>

            {/* Loads log */}
            {daily.dockets.length > 0 && (
              <div>
                <div className="label-tag mb-2">All Loads</div>
                <div className="overflow-x-auto border border-industrial">
                  <table className="w-full text-xs" data-testid="daily-loads-table">
                    <thead className="border-b border-industrial bg-surface-2">
                      <tr className="text-left">
                        <th className="p-2 label-tag">Docket</th>
                        <th className="p-2 label-tag">Time</th>
                        <th className="p-2 label-tag">Customer</th>
                        <th className="p-2 label-tag">Site</th>
                        <th className="p-2 label-tag">Mix</th>
                        <th className="p-2 label-tag">Truck</th>
                        <th className="p-2 label-tag text-right">m³</th>
                        <th className="p-2 label-tag text-right">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {daily.dockets.map((d) => (
                        <tr key={d.docket_no} className="border-b border-industrial">
                          <td className="p-2 mono">{d.docket_no}</td>
                          <td className="p-2 mono">{formatLocalTime(d.loaded_at)}</td>
                          <td className="p-2">{d.customer_name}</td>
                          <td className="p-2 text-muted-industrial truncate max-w-[220px]" title={d.site_address}>{d.site_address}</td>
                          <td className="p-2 mono">{d.mix_code}</td>
                          <td className="p-2 mono">{d.truck_number}</td>
                          <td className="p-2 mono text-right">{Number(d.quantity_m3 || 0).toFixed(1)}</td>
                          <td className="p-2 mono text-right">${Number(d.total || 0).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Cancelled log */}
            {daily.cancelled.length > 0 && (
              <div>
                <div className="label-tag mb-2 text-red-400">Cancelled Loads</div>
                <div className="overflow-x-auto border border-red-800/60">
                  <table className="w-full text-xs" data-testid="daily-cancelled-table">
                    <thead className="border-b border-red-800/60 bg-red-950/20">
                      <tr className="text-left">
                        <th className="p-2 label-tag">Docket</th>
                        <th className="p-2 label-tag">Customer</th>
                        <th className="p-2 label-tag">Mix · m³</th>
                        <th className="p-2 label-tag">Reason</th>
                        <th className="p-2 label-tag">By</th>
                      </tr>
                    </thead>
                    <tbody>
                      {daily.cancelled.map((c) => (
                        <tr key={c.docket_no} className="border-b border-red-800/40">
                          <td className="p-2 mono">{c.docket_no}</td>
                          <td className="p-2">{c.customer_name}</td>
                          <td className="p-2 mono">{c.mix_code} · {Number(c.quantity_m3 || 0).toFixed(1)}m³</td>
                          <td className="p-2 text-muted-industrial">{c.cancel_reason || "—"}</td>
                          <td className="p-2 mono text-muted-industrial">{c.cancelled_by || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {daily.totals.loads === 0 && daily.cancelled.length === 0 && (
              <div className="border border-dashed border-industrial p-6 text-center text-muted-industrial text-sm">
                No loads recorded on {reportDate}.
              </div>
            )}
          </>
        )}
      </div>

      {/* ─── ROLLING SUMMARY (14-day) ─────────────────────────────────────── */}
      {summary && (
        <>
          <div className="flex items-end justify-between" data-print-hide>
            <div>
              <div className="label-tag">TRENDING</div>
              <h2 className="heading text-xl font-black uppercase">
                Last <span className="text-primary-orange">{summaryDays} days</span>
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="card-industrial p-5" data-testid="daily-production-chart">
              <div className="label-tag mb-3">Daily Production (m³)</div>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={summary.daily_production}>
                  <CartesianGrid stroke="#27272A" vertical={false} />
                  <XAxis dataKey="date" tick={{ fill: "#A1A1AA", fontSize: 10, fontFamily: "JetBrains Mono" }} />
                  <YAxis tick={{ fill: "#A1A1AA", fontSize: 10, fontFamily: "JetBrains Mono" }} />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Bar dataKey="m3" fill={CHART_COLOR} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="card-industrial p-5" data-testid="truck-utilisation-chart">
              <div className="label-tag mb-3">Truck Utilisation (m³ delivered)</div>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={summary.truck_utilisation} layout="vertical">
                  <CartesianGrid stroke="#27272A" horizontal={false} />
                  <XAxis type="number" tick={{ fill: "#A1A1AA", fontSize: 10, fontFamily: "JetBrains Mono" }} />
                  <YAxis dataKey="truck" type="category" tick={{ fill: "#A1A1AA", fontSize: 10, fontFamily: "JetBrains Mono" }} width={60} />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Bar dataKey="m3" fill={CHART_COLOR} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="card-industrial p-5" data-testid="customer-sales-panel">
              <div className="label-tag mb-3">Customer Sales</div>
              {summary.customer_sales.length === 0 && <div className="text-sm text-muted-industrial">No sales data.</div>}
              {summary.customer_sales.map((c) => (
                <div key={c.customer} className="flex justify-between border-b border-industrial py-2 text-sm">
                  <span className="truncate pr-2">{c.customer}</span>
                  <span className="mono text-primary-orange">${c.sales.toFixed(2)}</span>
                </div>
              ))}
            </div>

            <div className="card-industrial p-5" data-testid="inventory-usage-panel">
              <div className="label-tag mb-3">Inventory Levels</div>
              {summary.inventory.map((i) => {
                const low = i.quantity < i.low_stock_threshold;
                const bulk = ["cement","sand","man_dust","aggregate_7mm","aggregate_10mm","aggregate_14mm","aggregate_20mm","exposed_rock"].includes(i.category);
                const qty = bulk ? (i.quantity || 0) / 1000 : i.quantity;
                const unit = bulk ? "t" : i.unit;
                return (
                  <div key={i.id} className="border-b border-industrial py-2">
                    <div className="flex justify-between text-sm">
                      <span>{i.name}</span>
                      <span className={`mono ${low ? "text-primary-orange" : ""}`}>{qty.toLocaleString(undefined, { maximumFractionDigits: bulk ? 2 : 0 })} {unit}</span>
                    </div>
                    <div className="h-1 bg-surface-2 mt-1"><div className="h-full bg-primary-orange" style={{ width: `${Math.min(100, (i.quantity / (i.low_stock_threshold * 3)) * 100)}%` }} /></div>
                  </div>
                );
              })}
            </div>

            <div className="card-industrial p-5" data-testid="outstanding-panel">
              <div className="label-tag mb-1">Outstanding</div>
              <div className="mono text-3xl font-bold text-primary-orange mb-4">${summary.outstanding_total.toFixed(2)}</div>
              {summary.outstanding_invoices.map((i) => (
                <div key={i.id} className="flex justify-between border-b border-industrial py-2 text-sm">
                  <div><div className="mono">{i.invoice_no}</div><div className="text-xs text-muted-industrial">{i.customer_name}</div></div>
                  <span className="mono">${i.total.toFixed(2)}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* ─── EFT ORDERS ───────────────────────────────────────────────────── */}
      <div className="flex items-end justify-between flex-wrap gap-3" data-testid="eft-report-heading">
        <div>
          <div className="label-tag">PAYMENTS</div>
          <h2 className="heading text-xl font-black uppercase">EFT <span className="text-primary-orange">orders</span></h2>
        </div>
        <div className="flex items-center gap-2" data-print-hide>
          <Popover open={eftStartOpen} onOpenChange={setEftStartOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" className="rounded-none border-industrial mono text-xs h-9" data-testid="eft-start-btn"><CalIcon className="w-3.5 h-3.5 mr-1" />{eftStart}</Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0"><Calendar mode="single" selected={new Date(eftStart + "T00:00:00")} onSelect={(d) => { if (d) { setEftStart(toISO(d)); setEftStartOpen(false); } }} /></PopoverContent>
          </Popover>
          <span className="text-muted-industrial text-xs">to</span>
          <Popover open={eftEndOpen} onOpenChange={setEftEndOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" className="rounded-none border-industrial mono text-xs h-9" data-testid="eft-end-btn"><CalIcon className="w-3.5 h-3.5 mr-1" />{eftEnd}</Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0"><Calendar mode="single" selected={new Date(eftEnd + "T00:00:00")} onSelect={(d) => { if (d) { setEftEnd(toISO(d)); setEftEndOpen(false); } }} /></PopoverContent>
          </Popover>
          <Button variant="outline" className="rounded-none border-industrial text-xs h-9" onClick={() => window.print()} data-testid="eft-print-btn"><Printer className="w-3.5 h-3.5 mr-1" />Print</Button>
        </div>
      </div>

      <div className="card-industrial p-0 overflow-hidden" data-testid="eft-report-table">
        <div className="flex justify-between items-center px-4 py-3 border-b border-industrial bg-surface-2/40">
          <span className="label-tag">{eft?.count || 0} EFT order{(eft?.count || 0) === 1 ? "" : "s"}</span>
          <span className="mono text-primary-orange font-bold" data-testid="eft-total">${(eft?.total || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-muted-industrial border-b border-industrial">
                <th className="p-2">Date</th><th className="p-2">Customer</th><th className="p-2">Mix</th>
                <th className="p-2 text-right">m³</th><th className="p-2">Site / PO</th><th className="p-2">Status</th><th className="p-2 text-right">Value (ex GST)</th>
              </tr>
            </thead>
            <tbody>
              {(eft?.rows || []).map((r, i) => (
                <tr key={i} className="border-b border-industrial/60" data-testid={`eft-row-${i}`}>
                  <td className="p-2 mono text-xs whitespace-nowrap">{r.date}</td>
                  <td className="p-2 truncate max-w-[200px]" title={r.customer_name}>{r.customer_name}</td>
                  <td className="p-2 mono text-xs">{r.mix_code || "—"}</td>
                  <td className="p-2 text-right mono">{r.quantity_m3}</td>
                  <td className="p-2 text-xs text-muted-industrial truncate max-w-[180px]">{[r.site_name, r.po_number].filter(Boolean).join(" · ") || "—"}</td>
                  <td className="p-2 text-xs uppercase">{r.status}</td>
                  <td className="p-2 text-right mono">${(r.amount || 0).toFixed(2)}</td>
                </tr>
              ))}
              {(!eft || eft.count === 0) && (
                <tr><td colSpan={7} className="p-6 text-center text-muted-industrial text-sm">No EFT orders in this period.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, accent, red }) {
  return (
    <div className="border border-industrial px-3 py-2 bg-surface-2/30">
      <div className="label-tag text-[9px]">{label}</div>
      <div className={`mono text-lg font-bold leading-tight mt-0.5 ${red ? "text-red-400" : accent ? "text-primary-orange" : "text-ink"}`}>{value}</div>
    </div>
  );
}

function BreakdownTable({ title, testid, rows, keyField }) {
  return (
    <div className="border border-industrial" data-testid={testid}>
      <div className="label-tag px-3 py-2 border-b border-industrial bg-surface-2/40">{title}</div>
      {rows.length === 0 && <div className="p-4 text-xs text-muted-industrial">No data.</div>}
      {rows.length > 0 && (
        <table className="w-full text-xs">
          <thead className="border-b border-industrial">
            <tr className="text-left">
              <th className="p-2 label-tag">{keyField}</th>
              <th className="p-2 label-tag text-right">Loads</th>
              <th className="p-2 label-tag text-right">m³</th>
              <th className="p-2 label-tag text-right">Revenue</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r[keyField] + "-" + i} className="border-b border-industrial last:border-b-0">
                <td className="p-2 truncate max-w-[160px]" title={r[keyField]}>{r[keyField]}</td>
                <td className="p-2 mono text-right">{r.loads}</td>
                <td className="p-2 mono text-right">{Number(r.m3 || 0).toFixed(1)}</td>
                <td className="p-2 mono text-right text-primary-orange">${Number(r.revenue || 0).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}



// ─── MONTHLY LOADS CALENDAR CARD ────────────────────────────────────
// v3.11 · Wall-calendar report: for any month, shows daily loads + m³ and a
// grand monthly total. Rendered live in the browser, plus a "Print PDF"
// action that downloads the landscape A4 sheet.
function MonthlyLoadsCard({ plantId }) {
  const [month, setMonth] = useState(() => localToday().slice(0, 7));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams({ month });
      if (plantId) q.set("plant_id", plantId);
      const { data } = await api.get(`/reports/monthly-calendar?${q}`);
      setData(data);
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [month, plantId]);
  useEffect(() => { load(); }, [load]);

  const shiftMonth = (delta) => {
    const [y, m] = month.split("-").map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    setMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  };
  const openPdf = () => {
    const q = new URLSearchParams({ month });
    if (plantId) q.set("plant_id", plantId);
    window.open(apiHref(`/reports/monthly-calendar.pdf?${q}`), "_blank");
  };

  const daysByNum = new Map((data?.days || []).map((d) => [d.day, d]));
  const firstOfMonth = data ? new Date(data.days[0].date + "T00:00:00") : null;
  const lastDay = data ? data.days.length : 0;
  const firstWeekdaySun = firstOfMonth ? firstOfMonth.getDay() : 0;
  const cells = [];
  for (let i = 0; i < firstWeekdaySun; i++) cells.push({ spill: true });
  for (let d = 1; d <= lastDay; d++) cells.push({ day: d, ...daysByNum.get(d) });
  while (cells.length % 7 !== 0) cells.push({ empty: true });
  const weeks = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  const dayLabels = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];

  return (
    <div className="card-industrial p-5 space-y-4" data-testid="monthly-loads-card">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-tag">MONTHLY LOADS CALENDAR</div>
          <h2 className="heading text-xl font-black uppercase">{data?.month_name || month}</h2>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => shiftMonth(-1)}
            data-testid="monthly-prev"
            className="border border-industrial p-2 hover:border-primary-orange hover:text-primary-orange"
            title="Previous month"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            data-testid="monthly-month-input"
            className="border border-industrial bg-transparent px-2 py-1.5 uppercase text-xs mono tracking-widest text-ink"
          />
          <button
            type="button"
            onClick={() => shiftMonth(1)}
            data-testid="monthly-next"
            className="border border-industrial p-2 hover:border-primary-orange hover:text-primary-orange"
            title="Next month"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
          <Button
            onClick={openPdf}
            data-testid="monthly-pdf-btn"
            className="rounded-none bg-primary-orange hover:bg-[rgb(var(--primary-hover))] uppercase tracking-widest text-xs font-bold text-black"
          >
            <FileDown className="w-3.5 h-3.5 mr-1" /> Print PDF
          </Button>
        </div>
      </div>

      {loading && <div className="mono text-xs text-muted-industrial uppercase">Loading…</div>}

      {!loading && data && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="monthly-totals-strip">
          {[
            { k: "loads", label: "Loads", v: data.total_loads, tid: "strip-loads" },
            { k: "m3", label: "Metres m³", v: data.total_m3.toFixed(2), tid: "strip-m3" },
            { k: "wait", label: "Waiting Time $", v: `$${(data.total_wait_charge || 0).toFixed(2)}`, tid: "strip-wait",
              sub: `${data.total_wait_minutes || 0} min over ${data.total_wait_loads || 0} load${data.total_wait_loads === 1 ? "" : "s"}`, hot: (data.total_wait_charge || 0) > 0 },
            { k: "avg", label: "Avg Load m³", v: data.total_loads ? (data.total_m3 / data.total_loads).toFixed(2) : "0.00", tid: "strip-avg" },
          ].map((s) => (
            <div key={s.k} className={`border p-3 ${s.hot ? "border-primary-orange bg-primary-orange/5" : "border-industrial"}`} data-testid={s.tid}>
              <div className="mono text-[10px] uppercase tracking-widest text-muted-industrial">{s.label}</div>
              <div className={`heading text-2xl font-black leading-none mt-1 ${s.hot ? "text-primary-orange" : "text-ink"}`}>{s.v}</div>
              {s.sub && <div className="mono text-[10px] text-muted-industrial mt-0.5">{s.sub}</div>}
            </div>
          ))}
        </div>
      )}

      {!loading && data && (
        <div className="grid grid-cols-7 gap-1" data-testid="monthly-grid">
          {dayLabels.map((lb) => (
            <div key={lb} className="mono text-[10px] tracking-widest uppercase text-muted-industrial text-center py-1">{lb}</div>
          ))}
          {weeks.map((week, wi) => week.map((cell, ci) => {
            const isLastCell = wi === weeks.length - 1 && ci === 6;
            if (isLastCell) {
              return (
                <div key={`mt-${wi}-${ci}`} className="border-2 border-ink p-2 bg-surface-2" data-testid="monthly-total-cell">
                  <div className="mono text-[10px] font-bold uppercase tracking-widest text-ink">Monthly Total</div>
                  <div className="mt-2 flex justify-between mono text-[10px]">
                    <span className="text-muted-industrial">LOADS:</span>
                    <span className="text-primary-orange font-bold" data-testid="monthly-total-loads">{data.total_loads}</span>
                  </div>
                  <div className="flex justify-between mono text-[10px]">
                    <span className="text-muted-industrial">METRES:</span>
                    <span className="text-primary-orange font-bold" data-testid="monthly-total-m3">{data.total_m3.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between mono text-[10px] border-t border-industrial mt-1 pt-1">
                    <span className="text-muted-industrial">WAITING:</span>
                    <span className="text-primary-orange font-bold" data-testid="monthly-total-wait">${(data.total_wait_charge || 0).toFixed(2)}</span>
                  </div>
                </div>
              );
            }
            if (cell.empty || cell.spill) {
              return <div key={`sp-${wi}-${ci}`} className="border border-industrial/40 p-2 min-h-[68px] opacity-30" />;
            }
            return (
              <div key={`d-${wi}-${ci}`} className="border border-industrial p-2 min-h-[68px]" data-testid={`monthly-day-${cell.day}`}>
                <div className="flex justify-between items-start">
                  <span className="mono font-bold text-ink">{cell.day}</span>
                  <span className="mono text-[9px] uppercase text-muted-industrial">{dayLabels[(firstWeekdaySun + cell.day - 1) % 7]}</span>
                </div>
                <div className="mt-2 flex justify-between mono text-[10px]">
                  <span className="text-muted-industrial">LOADS:</span>
                  <span className="text-ink">{cell.loads || 0}</span>
                </div>
                <div className="flex justify-between mono text-[10px]">
                  <span className="text-muted-industrial">METRES:</span>
                  <span className="text-ink">{(cell.m3 || 0).toFixed(2)}</span>
                </div>
                {cell.wait_charge > 0 && (
                  <div className="flex justify-between mono text-[10px]" title={`${cell.wait_minutes} chargeable minutes across ${cell.wait_loads} load(s)`}>
                    <span className="text-muted-industrial">WAIT:</span>
                    <span className="text-primary-orange font-bold">${cell.wait_charge.toFixed(2)}</span>
                  </div>
                )}
              </div>
            );
          }))}
        </div>
      )}
    </div>
  );
}
